import { useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { Check, ImageOff, Loader2, Play, Upload, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { type AttachRdoPhotoInput, listPosts } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { compressImage } from "@/features/feed/utils/image.ts";
import {
	canSelectMorePhotos,
	formatPhotoCount,
	isAllowedRdoPhoto,
	photoSlotsLeft,
	RDO_PHOTO_TYPES,
	togglePhotoSelection,
} from "../../utils/photos.ts";

type PickerTab = "diary" | "upload";

interface DiaryMedia {
	id: string;
	postTitle: string;
	previewUrl: string;
	isVideo: boolean;
}

interface PhotoPickerDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	constructionId: string;
	reportDate: string;
	currentCount: number;
	attachedMediaIds: ReadonlySet<string>;
	onAttach: (inputs: AttachRdoPhotoInput[]) => Promise<unknown>;
}

const MAX_POSTS = 100;

function useDiaryMedia(
	constructionId: string,
	reportDate: string,
	enabled: boolean,
) {
	return useQuery({
		queryKey: ["rdo-diary-media", constructionId, reportDate],
		queryFn: async (): Promise<DiaryMedia[]> => {
			const { items } = await listPosts({
				constructionId,
				visibility: "all",
				occurredFrom: reportDate,
				occurredTo: reportDate,
				pageSize: MAX_POSTS,
			});
			return items.flatMap((post) =>
				post.media.flatMap((media) => {
					if (media.processing_status !== "ready") return [];
					const isVideo = media.type === "video";
					const previewUrl = isVideo ? media.thumbnail_url : media.url;
					if (!previewUrl) return [];
					return [{ id: media.id, postTitle: post.title, previewUrl, isVideo }];
				}),
			);
		},
		enabled,
		staleTime: 30_000,
	});
}

export function PhotoPickerDialog({
	open,
	onOpenChange,
	constructionId,
	reportDate,
	currentCount,
	attachedMediaIds,
	onAttach,
}: PhotoPickerDialogProps) {
	const [tab, setTab] = useState<PickerTab>("diary");
	const [selected, setSelected] = useState<string[]>([]);
	const [file, setFile] = useState<File | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [caption, setCaption] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const diary = useDiaryMedia(constructionId, reportDate, open);
	const slotsLeft = photoSlotsLeft(currentCount);
	const projectedCount = currentCount + selected.length + (file ? 1 : 0);

	useEffect(() => {
		if (!open) {
			setTab("diary");
			setSelected([]);
			setFile(null);
			setCaption("");
			setError(null);
			setBusy(false);
		}
	}, [open]);

	useEffect(() => {
		if (!file) {
			setPreviewUrl(null);
			return;
		}
		const url = URL.createObjectURL(file);
		setPreviewUrl(url);
		return () => URL.revokeObjectURL(url);
	}, [file]);

	const available = useMemo(
		() => (diary.data ?? []).filter((media) => !attachedMediaIds.has(media.id)),
		[diary.data, attachedMediaIds],
	);

	function toggle(id: string) {
		setSelected((current) => togglePhotoSelection(current, id, currentCount));
	}

	function pickFile(files: FileList | null) {
		const next = files?.[0];
		if (!next) return;
		if (!isAllowedRdoPhoto(next)) {
			setError("Envie um PNG ou JPEG de até 10 MB.");
			return;
		}
		setError(null);
		setFile(next);
	}

	async function confirm() {
		setError(null);
		setBusy(true);
		try {
			if (tab === "diary") {
				await onAttach(selected.map((postMediaId) => ({ postMediaId })));
			} else if (file) {
				const compressed = await compressImage(file).catch(() => file);
				await onAttach([
					{ file: compressed, caption: caption.trim() || undefined },
				]);
			}
			onOpenChange(false);
		} catch {
			setError("Não foi possível adicionar a foto. Tente novamente.");
		} finally {
			setBusy(false);
		}
	}

	const canConfirm =
		!busy &&
		slotsLeft > 0 &&
		(tab === "diary" ? selected.length > 0 : !!file);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="photo-picker__dialog">
				<DialogHeader>
					<DialogTitle>Adicionar fotos</DialogTitle>
					<DialogDescription>
						Até seis fotos por atividade. Escolha do diário do dia ou envie uma
						imagem.
					</DialogDescription>
				</DialogHeader>

				<div className="photo-picker__tabs" role="tablist">
					<button
						type="button"
						role="tab"
						aria-selected={tab === "diary"}
						className={clsx("photo-picker__tab", tab === "diary" && "is-active")}
						onClick={() => setTab("diary")}
					>
						Do diário
					</button>
					<button
						type="button"
						role="tab"
						aria-selected={tab === "upload"}
						className={clsx(
							"photo-picker__tab",
							tab === "upload" && "is-active",
						)}
						onClick={() => setTab("upload")}
					>
						Enviar
					</button>
					<span className="photo-picker__counter" data-testid="photo-counter">
						{formatPhotoCount(projectedCount)}
					</span>
				</div>

				{tab === "diary" ? (
					<div className="photo-picker__panel">
						{diary.isPending ? (
							<div className="photo-picker__state">
								<Loader2 className="photo-picker__spinner" />
							</div>
						) : diary.isError ? (
							<div className="photo-picker__state">
								<p>Não foi possível carregar as mídias do diário.</p>
								<Button variant="outline" size="sm" onClick={() => diary.refetch()}>
									Tentar novamente
								</Button>
							</div>
						) : available.length === 0 ? (
							<div className="photo-picker__state">
								<ImageOff aria-hidden />
								<p>Nenhuma mídia do diário disponível para esta data.</p>
							</div>
						) : (
							<div className="photo-picker__grid">
								{available.map((media) => {
									const isSelected = selected.includes(media.id);
									const disabled =
										!isSelected && !canSelectMorePhotos(selected, currentCount);
									return (
										<button
											key={media.id}
											type="button"
											className={clsx(
												"photo-picker__tile",
												isSelected && "is-selected",
											)}
											aria-pressed={isSelected}
											aria-label={`${media.isVideo ? "Vídeo" : "Foto"} de ${media.postTitle}`}
											disabled={disabled || busy}
											onClick={() => toggle(media.id)}
										>
											<img src={media.previewUrl} alt="" />
											{media.isVideo && (
												<span className="photo-picker__play" aria-hidden>
													<Play />
												</span>
											)}
											{isSelected && (
												<span className="photo-picker__check" aria-hidden>
													<Check />
												</span>
											)}
										</button>
									);
								})}
							</div>
						)}
					</div>
				) : (
					<div className="photo-picker__panel">
						<input
							ref={inputRef}
							type="file"
							accept={RDO_PHOTO_TYPES.join(",")}
							aria-label="Arquivo da foto"
							className="photo-picker__file-input"
							onChange={(event) => {
								pickFile(event.target.files);
								event.target.value = "";
							}}
						/>
						{file && previewUrl ? (
							<div className="photo-picker__upload">
								<div className="photo-picker__upload-preview">
									<img src={previewUrl} alt="Pré-visualização" />
									<button
										type="button"
										className="photo-picker__upload-remove"
										aria-label="Remover arquivo"
										onClick={() => setFile(null)}
										disabled={busy}
									>
										<X />
									</button>
								</div>
								<Input
									aria-label="Legenda"
									placeholder="Legenda (opcional)"
									value={caption}
									maxLength={200}
									onChange={(event) => setCaption(event.target.value)}
									disabled={busy}
								/>
							</div>
						) : (
							<button
								type="button"
								className="photo-picker__dropzone"
								onClick={() => inputRef.current?.click()}
								disabled={slotsLeft === 0 || busy}
							>
								<Upload aria-hidden />
								<span>Escolher imagem</span>
								<span className="photo-picker__dropzone-hint">
									PNG ou JPEG de até 10 MB
								</span>
							</button>
						)}
					</div>
				)}

				{slotsLeft === 0 && (
					<p className="photo-picker__limit">
						Esta atividade já tem o máximo de seis fotos.
					</p>
				)}
				{error && (
					<p role="alert" className="form-error-alert">
						{error}
					</p>
				)}

				<DialogFooter>
					<Button
						type="button"
						variant="ghost"
						onClick={() => onOpenChange(false)}
						disabled={busy}
					>
						Cancelar
					</Button>
					<Button type="button" onClick={confirm} disabled={!canConfirm}>
						{busy && <Loader2 className="photo-picker__spinner" />}
						{tab === "diary"
							? `Adicionar${selected.length > 0 ? ` (${selected.length})` : ""}`
							: "Enviar foto"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
